import React, { useEffect, useState } from "react";
import liff from "@line/liff";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../components/Header";
import DeliverTimeCard from "../components/DeliverTimeCard";
import DraftNotFoundView from "../components/DraftNotFoundView";
import { CalendarDay, Order } from "../utils/type";
import {
  getAvailableSlots,
  setDraftDeliveryTime,
  getQuickOrderErrorMessage,
  getDraftOrder,
  mapDraftToOrder,
  isSelectedSlotPastForToday,
  isSelectedDateClosed,
  AvailableSlotsDay,
  DraftOrderResponse,
} from "../utils/mockApi";
import { Button, View } from "tamagui";
import { colors } from "@styles/colors";
import * as basicStyle from "@styles/index";
import { appToast } from "@components/Toast";
import { useAuthReady } from "@provider/LineLoginProvider";
import { useLineAuth } from "@utils/store/auth/useLineAuth";
import { useUtilsStore } from "@utils/store/utils/useUtilsTool";
import api from "@services/axios/apiManager";
import { flushBeforeClose } from "@lib/tracker";

const OrderExpiredImg = require("@styles/images/order_expired.png") as string;

const EMPTY_ORDER: Order = {
  items: [{ id: 1, weight: "20", type: "gas", quantity: 1 }],
  addressId: "1",
  deliveryType: "scheduled",
  deliveryDate: null,
  deliverySlotId: null,
  deliverySlotSource: null,
  deliverySlotLabel: null,
  taxId: "",
  carrier: "",
  phone: "",
  note: "",
};

/**
 * 快速修改配送時間：僅選擇配送方式 + 日期 + 時段，PATCH 草稿的 delivery。
 * 路徑：/quickOrder/:draftId/delivery
 */
export default function QuickOrderDeliveryOnly() {
  const authReady = useAuthReady();
  const { lineAccessToken } = useLineAuth();
  const { isLoading: isAuthLoading } = useUtilsStore();
  const { draftId } = useParams<{ draftId: string }>();
  const navigate = useNavigate();
  const [availableSlots, setAvailableSlots] = useState<AvailableSlotsDay[]>([]);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [calendarDays, setCalendarDays] = useState<(CalendarDay | null)[]>([]);
  const [order, setOrder] = useState<Order>(EMPTY_ORDER);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderStatus, setOrderState] = useState<string | null>(null);
  const [isFetchingDraft, setIsFetchingDraft] = useState(true);
  const [jwtEnsured, setJwtEnsured] = useState(false);
  const [draftData, setDraftData] = useState<DraftOrderResponse | null>(null);
  const [hasAutoSelectedSlot, setHasAutoSelectedSlot] = useState(false);
  const [isDraftExpired, setIsDraftExpired] = useState(false);

  const isDraftNotEditable = orderStatus != null && orderStatus !== "待確認";

  const isDraftOrderExpiredError = (err: unknown): boolean => {
    const response = (err as any)?.response;
    const responseStatus = response?.status;
    const bodyStatus = response?.data?.status ?? response?.data?.statusCode;
    const rawMessage = response?.data?.message ?? (err as any)?.message ?? "";
    const message = String(rawMessage);

    return responseStatus === 404 || bodyStatus === 404 || message.includes("Draft order not found") || message.includes("找不到草稿");
  };

  /**
   * 確保在進入「快速修改配送時間」前已取得 jwtToken。
   * 邏輯與主 QuickOrder 頁面一致。
   */
  useEffect(() => {
    if (!authReady || jwtEnsured) return;

    const ensureJwtToken = async () => {
      if (typeof window === "undefined") {
        setJwtEnsured(true);
        return;
      }

      const existingJwt = sessionStorage.getItem("jwtToken");
      if (existingJwt) {
        setJwtEnsured(true);
        return;
      }

      if (!lineAccessToken) {
        setJwtEnsured(true);
        return;
      }

      try {
        const res = await api.post("/app-api/line/login", { accessToken: lineAccessToken }, { isAuth: false });
        const jwtToken = (res as any)?.data?.data?.jwtToken;
        if (jwtToken) {
          sessionStorage.setItem("jwtToken", jwtToken);
        }
      } catch (_err) {
        // 若補發 jwt 失敗，後續真正的業務 API 會回傳更明確的錯誤
      } finally {
        setJwtEnsured(true);
      }
    };

    void ensureJwtToken();
  }, [authReady, lineAccessToken, jwtEnsured]);

  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const generateCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = firstDay.getDay();
    const days: (CalendarDay | null)[] = [];
    for (let i = 0; i < startPadding; i++) days.push(null);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(year, month, day);
      const dateStr = formatDate(date);
      const dayData = availableSlots.find((d) => d.delivery_date === dateStr);
      if (dayData) {
        const isAvailable = !dayData.is_closed_all_day && dayData.slots.some((s) => s.available_capacity > 0);
        days.push({
          day,
          date: dateStr,
          fullDate: date,
          isAvailable,
          isPast: false,
          isClosed: dayData.is_closed_all_day,
        });
      } else {
        days.push({
          day,
          date: dateStr,
          fullDate: date,
          isAvailable: false,
          isPast: date < today,
          isClosed: false,
        });
      }
    }
    setCalendarDays(days);
  };

  useEffect(() => {
    generateCalendar();
  }, [currentMonth, availableSlots]);

  useEffect(() => {
    if (!authReady || !jwtEnsured) return;
    getAvailableSlots()
      .then((list) => {
        setAvailableSlots(list);
        if (list.length > 0) {
          const first = new Date(list[0].delivery_date);
          setCurrentMonth(new Date(first.getFullYear(), first.getMonth(), 1));
        }
      })
      .catch(() => {});
  }, [authReady, jwtEnsured]);

  /** 載入草稿：取得 order_status 判斷可否修改，並將草稿的配送資訊載入 order。等最外層登入檢查結束後才開始，避免與 auth loading overlay 重疊。 */
  useEffect(() => {
    if (isAuthLoading || !authReady || !jwtEnsured || !draftId) return;
    setIsDraftExpired(false);
    getDraftOrder(draftId)
      .then((draft) => {
        const status = draft?.order_infos?.order_status ?? null;
        setOrderState(status);
        if (draft && status === "待確認") {
          const { order: mappedOrder } = mapDraftToOrder(draft);
          setDraftData(draft);
          setOrder(mappedOrder);
        }
      })
      .catch((err) => {
        if (isDraftOrderExpiredError(err)) {
          setIsDraftExpired(true);
          return;
        }
        setOrderState("not_found");
        appToast.error(getQuickOrderErrorMessage(err));
      })
      .finally(() => {
        setIsFetchingDraft(false);
      });
  }, [isAuthLoading, authReady, jwtEnsured, draftId]);

  /**
   * 根據草稿的 delivery_time_stamp，自動對應並預選配送時段（僅「預約配送」且有時間時）：
   * - 僅在草稿可編輯、草稿為預約配送、有有效 delivery_time_stamp 且後端未回 slot_id/slot_source 時啟用
   * - 使用 availableSlots 中對應日期的 slot，找出時間區間涵蓋該時間點的那一筆
   * - 若當日無資料、無符合 slot、額滿或已過期，則不預選
   */
  useEffect(() => {
    if (!draftData) return;
    if (isDraftNotEditable) return;
    if (hasAutoSelectedSlot) return;
    if (!availableSlots.length) return;

    const info = draftData.order_infos;
    if (info?.delivery_type_enum === "立即配送") {
      setHasAutoSelectedSlot(true);
      return;
    }

    // 若後端已回傳 slot_id/slot_source，沿用 mapDraftToOrder 的結果，不再覆寫
    const hasBackendSlot = typeof info?.slot_id === "number" && (info.slot_source === "weekly" || info.slot_source === "special");
    if (hasBackendSlot) {
      setHasAutoSelectedSlot(true);
      return;
    }

    const deliveryTimestamp = info?.delivery_time_stamp;
    if (!deliveryTimestamp || typeof deliveryTimestamp !== "string") return;
    const trimmed = deliveryTimestamp.trim();
    if (!trimmed) return;

    const ts = new Date(trimmed);
    if (Number.isNaN(ts.getTime())) return;

    const year = ts.getFullYear();
    const month = String(ts.getMonth() + 1).padStart(2, "0");
    const day = String(ts.getDate()).padStart(2, "0");
    const hours = ts.getHours();
    const minutes = ts.getMinutes();
    const targetMinutes = hours * 60 + minutes;
    const dateStr = `${year}-${month}-${day}`;

    const dayData = availableSlots.find((d) => d.delivery_date === dateStr);
    if (!dayData || dayData.is_closed_all_day || !dayData.slots?.length) return;

    const matchedSlot = dayData.slots.find((slot) => {
      const [sh, sm] = slot.start_time.split(":").map(Number);
      const [eh, em] = slot.end_time.split(":").map(Number);
      const startMinutes = (sh || 0) * 60 + (sm || 0);
      const endMinutes = (eh || 0) * 60 + (em || 0);
      return targetMinutes >= startMinutes && targetMinutes < endMinutes;
    });

    if (!matchedSlot) return;

    const availableCapacity = matchedSlot.available_capacity ?? 0;
    if (availableCapacity <= 0) return;
    if (isSelectedSlotPastForToday(availableSlots, dateStr, matchedSlot.slot_id)) return;

    setOrder((prev) => ({
      ...prev,
      deliveryType: "scheduled",
      deliveryDate: dateStr,
      deliverySlotId: matchedSlot.slot_id,
      deliverySlotSource: matchedSlot.slot_source,
      deliverySlotLabel: `${matchedSlot.start_time} - ${matchedSlot.end_time}`,
    }));
    setHasAutoSelectedSlot(true);
  }, [draftData, availableSlots, hasAutoSelectedSlot, isDraftNotEditable]);

  const handleSubmit = async () => {
    if (!draftId) {
      appToast.error("缺少草稿單號");
      return;
    }
    if (order.deliveryType === "scheduled") {
      if (!order.deliveryDate && (!order.deliverySlotId || !order.deliverySlotSource)) {
        appToast.error("請選擇預約配送的日期與時段。");
        return;
      }
      if (!order.deliveryDate) {
        appToast.error("請先選擇預約配送的日期。");
        return;
      }
      if (!order.deliverySlotId || !order.deliverySlotSource) {
        appToast.error("請選擇預約配送的時段。");
        return;
      }
      if (isSelectedDateClosed(availableSlots, order.deliveryDate)) {
        appToast.error("所選日期為公休日，請選擇其他日期。");
        return;
      }
      if (isSelectedSlotPastForToday(availableSlots, order.deliveryDate, order.deliverySlotId)) {
        appToast.error("所選配送時段已過期，請重新選擇。");
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const todayStr = formatDate(new Date());
      await setDraftDeliveryTime(draftId, {
        delivery_type_enum: order.deliveryType === "immediate" ? "立即配送" : "預約配送",
        delivery_date: order.deliveryType === "immediate" ? todayStr : order.deliveryDate!,
        ...(order.deliveryType === "scheduled" && order.deliverySlotId != null && order.deliverySlotSource
          ? { slot_id: order.deliverySlotId, slot_source: order.deliverySlotSource }
          : {}),
      });
      appToast.success("配送時間已更新");
      // 延遲關閉／導向，讓使用者先看到 toast（與編輯訂單流程一致）
      setTimeout(() => {
        try {
          if (liff.isInClient()) {
            flushBeforeClose(); // 離頁前強制 flush 佇列中的事件，避免 LIFF 關窗把最後幾筆事件丟掉
            liff.closeWindow();
          } else {
            navigate("/quickOrder/drafts");
          }
        } catch {
          navigate("/quickOrder/drafts");
        }
      }, 300);
    } catch (err: any) {
      if (isDraftOrderExpiredError(err)) {
        setIsDraftExpired(true);
        return;
      }
      appToast.error(getQuickOrderErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* 僅在「最外層登入檢查已結束」時才顯示頁內載入，避免與 UtilsProvider 的 Loading overlay 重疊 */}
      {(!authReady || !jwtEnsured) && !isAuthLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 10001 }}>
          <div className="bg-white rounded-2xl p-6 shadow-lg min-w-[200px] flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            <span className="text-sm font-medium text-gray-700">載入中...</span>
          </div>
        </div>
      )}
      {isSubmitting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 10000 }}>
          <div className="bg-white rounded-2xl p-6 shadow-lg min-w-[200px] flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            <span className="text-sm font-medium text-gray-700">正在更新配送時間...</span>
          </div>
        </div>
      )}

      {/* 僅在登入檢查結束後顯示，避免與最外層 auth 或頁內「載入中...」重疊 */}
      {isFetchingDraft && !isAuthLoading && authReady && jwtEnsured && (
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      )}

      {!isFetchingDraft && isDraftNotEditable && (
        <div className="flex-1 overflow-y-auto flex flex-col">
          <Header title="快速修改配送時間" />
          <DraftNotFoundView
            onBackToDrafts={() => navigate("/quickOrder/drafts")}
            title={orderStatus === "not_found" ? undefined : "此訂單無法修改配送時間"}
            description={orderStatus === "not_found" ? undefined : ""}
          />
        </div>
      )}

      {!isFetchingDraft && isDraftExpired && (
        <div className="flex-1 overflow-y-auto flex flex-col">
          <Header title="快速修改配送時間" />
          <DraftNotFoundView
            onBackToDrafts={() => {
              try {
                if (liff.isInClient()) {
                  flushBeforeClose(); // 離頁前強制 flush 佇列中的事件，避免 LIFF 關窗把最後幾筆事件丟掉
                  liff.closeWindow();
                } else {
                  navigate("/quickOrder/drafts");
                }
              } catch {
                navigate("/quickOrder/drafts");
              }
            }}
            title="訂單已過期"
            description="此草稿訂單已失效，請回到 LINE 聊天室重新發起一鍵下單。"
            buttonText="返回 LINE 聊天室重新下單"
            imageUri={OrderExpiredImg}
            imageAlt="訂單已過期"
          />
        </div>
      )}

      {!isFetchingDraft && !isDraftNotEditable && !isDraftExpired && (
        <div className="flex-1 overflow-y-auto">
          <Header title="快速修改配送時間" />
          <div className="max-w-md mx-auto px-4 py-4 space-y-4" style={{ marginBottom: "5.5em" }}>
            <DeliverTimeCard
              setCurrentMonth={setCurrentMonth}
              currentMonth={currentMonth}
              calendarDays={calendarDays}
              order={order}
              setOrder={setOrder}
              availableSlots={availableSlots}
            />
          </div>

          <View
            position="absolute"
            bottom={0}
            left={0}
            right={0}
            bg="#FFF"
            borderTopWidth={1}
            borderTopColor={colors.dividerGray}
            p="$4"
            zIndex={1000000}>
            <Button
              {...basicStyle.ShareBlueButton}
              background={colors.btnBlue}
              color="#FFF"
              size="$5"
              onPress={handleSubmit}
              disabled={isSubmitting}
              width="100%">
              確認
            </Button>
          </View>
        </div>
      )}
    </div>
  );
}
